import React, { useContext, useMemo } from 'react'
import moment from 'moment'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import RepeatIcon from '@mui/icons-material/Repeat'
import { BookingContext, SectionHeader } from './NewBooking'
import AnDateField from '../../../components/DateTime/DateField'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import AnDatePicker from '../../../components/DateTime/DatePicker'

const Recurrence = () => {
  const { formData, handleChange } = useContext(BookingContext)
  const recurrence = formData.recurrence || {
    enabled: false,
    frequency: '',
    interval: 1,
    days: [],
    until: null,
  }

  const pickupDate =
    formData?.pickup?.when?.exactTime ||
    formData?.pickup?.when?.windowStart ||
    null

  // 🧮 Calculate estimated number of journeys
  const journeyCount = useMemo(() => {
    if (!recurrence.enabled || !pickupDate || !recurrence.until) return 1

    const start = moment(pickupDate)
    const end = moment(recurrence.until)
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return 1

    switch (recurrence.frequency) {
      case 'Daily': {
        const selectedDays = recurrence.days || []
        if (selectedDays.length === 0) return 1

        let count = 0
        const current = start.clone()
        while (current.isSameOrBefore(end, 'day')) {
          const dayName = current.format('ddd')
          if (selectedDays.includes(dayName)) count++
          current.add(1, 'day')
        }
        return count
      }

      case 'Weekly': {
        const interval = recurrence.interval || 1
        const weeks = Math.floor(end.diff(start, 'weeks') / interval) + 1
        return Math.max(weeks, 1)
      }

      case 'Monthly': {
        const interval = recurrence.interval || 1
        const months = Math.floor(end.diff(start, 'months') / interval) + 1
        return Math.max(months, 1)
      }

      default:
        return 1
    }
  }, [recurrence, formData.pickup])

  const validStart = pickupDate && moment(pickupDate).isValid()
  const validEnd = recurrence.until && moment(recurrence.until).isValid()

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SectionHeader
          icon={<RepeatIcon fontSize="small" />}
          title="Recurrence"
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={recurrence?.enabled ?? false}
              onChange={(e) =>
                handleChange('recurrence', {
                  ...recurrence,
                  enabled: e.target.checked,
                  frequency: e.target.checked ? 'Daily' : '',
                })
              }
            />
          }
          label="Enable Recurrence"
        />
      </Grid>

      {recurrence.enabled && (
        <>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Frequency"
              value={recurrence.frequency || 'Daily'}
              onChange={(e) =>
                handleChange('recurrence', {
                  ...recurrence,
                  frequency: e.target.value,
                })
              }
            >
              <MenuItem value="Daily">Daily</MenuItem>
              <MenuItem value="Weekly">Weekly</MenuItem>
              <MenuItem value="Monthly">Monthly</MenuItem>
            </TextField>
          </Grid>

          {/* End date */}
          <Grid item xs={12} md={4}>
            <AnDatePicker
              label="Recurrence Ends"
              fullWidth
              value={recurrence.until}
              onChange={(val) =>
                handleChange('recurrence', {
                  ...recurrence,
                  until: val,
                })
              }
            />
          </Grid>

          {/* Daily: Day selection */}
          {recurrence.frequency === 'Daily' && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(
                  (day) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={recurrence.days?.includes(day) || false}
                          onChange={(e) => {
                            const newDays = e.target.checked
                              ? [...(recurrence.days || []), day]
                              : (recurrence.days || []).filter((d) => d !== day)
                            handleChange('recurrence', {
                              ...recurrence,
                              days: newDays,
                            })
                          }}
                        />
                      }
                      label={day}
                    />
                  )
                )}
              </Box>
            </Grid>
          )}

          {/* Weekly */}
          {recurrence.frequency === 'Weekly' && (
            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                fullWidth
                label="Repeat every X weeks"
                inputProps={{ min: 1 }}
                value={recurrence.interval || 1}
                onChange={(e) =>
                  handleChange('recurrence', {
                    ...recurrence,
                    interval: parseInt(e.target.value, 10),
                  })
                }
              />
            </Grid>
          )}

          {/* Monthly */}
          {recurrence.frequency === 'Monthly' && (
            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                fullWidth
                label="Repeat every X months"
                inputProps={{ min: 1 }}
                value={recurrence.interval || 1}
                onChange={(e) =>
                  handleChange('recurrence', {
                    ...recurrence,
                    interval: parseInt(e.target.value, 10),
                  })
                }
              />
            </Grid>
          )}

          {/* 🧾 Summary */}
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: '#f5f7fa',
                border: '1px solid #e0e0e0',
              }}
            >
              {validStart && validEnd ? (
                <>
                  🗓️ Based on your selection, this booking will create{' '}
                  <strong>{journeyCount}</strong>{' '}
                  {journeyCount === 1 ? 'journey' : 'journeys'} between{' '}
                  <strong>{moment(pickupDate).format('Do MMM YYYY')}</strong>{' '}
                  and{' '}
                  <strong>
                    {moment(recurrence.until).format('Do MMM YYYY')}
                  </strong>
                  .
                </>
              ) : (
                <>
                  🗓️ Select a pickup date and recurrence end date to see the
                  journey count.
                </>
              )}
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default Recurrence
